import { IconGraphy, Table } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { Typography, Badge, Tooltip } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { getDemoData } from 'utils/helpers';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import { CLAIM_FILE_STATUS_TABLE_BADGES } from 'data/constants/common_constants';
import { getProgramAppTypeTableMeta } from 'utils/programAppTypeDisplay';
import { formatClaimDetailCurrency } from 'utils/formatClaimDetailCurrency';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import { CLAIMS_TABLE_DEFAULT_VISIBLE_KEYS, CLAIMS_TABLE_STORAGE_KEY } from './claimTableConfig';

const LEAP_PURPLE = '#702572';
const DISABLED_ICON_COLOR = '#B8B0BC';

const BADGE_CLAIM_FILE_STATUS = CLAIM_FILE_STATUS_TABLE_BADGES;

const getClaimFileStatusMeta = (value) =>
  BADGE_CLAIM_FILE_STATUS.find((item) => item.value === value) || { badge: value || 'Unknown' };

const dateColumnOptions = (overrides = {}) => ({
  width: 104,
  minWidth: 104,
  maxWidth: 104,
  shrink: false,
  ...overrides,
});

export const ClaimTable = forwardRef(
  (
    {
      claims,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickClaim,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      onLoadFullDatasetTotal,
      isLoadingFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      loading = false,
    },
    ref,
  ) => {
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);

    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    const buildTableRow = (claim, index) => [
      claim.claim_name,
      isDemo ? getDemoData('tenant-name') : claim.tenant1,
      isDemo ? getDemoData('rider-id') : claim.rider_id,
      claim.claim_status,
      claim.claim_file_status,
      claim.app_type,
      claim.rider_damage_coverage_amount ? parseFloat(claim.rider_damage_coverage_amount) : 0,
      claim.total_indemnity_payments ? parseFloat(claim.total_indemnity_payments) : 0,
      claim.bond_issue_date,
      isDemo ? getDemoData('account-name') : claim.account_name,
      claim.monthly_rent ? parseFloat(claim.monthly_rent) : 0,
      isDemo ? getDemoData('landlord-name') : claim.landlord_name,
      claim.who_should_the_check_be_made_out_to || '',
      claim.denial_reason_1 || '',
      index,
    ];

    const tableData = useMemo(() => {
      if (!claims) return [];
      return claims.map((claim, index) => buildTableRow(claim, index));
    }, [claims, isDemo]);

    const goDetailPage = useCallback(
      (claim) => {
        if (claim) onClickClaim(claim);
      },
      [onClickClaim],
    );

    const formatCurrency = (value) => {
      const amount = Number(value);
      return Number.isFinite(amount)
        ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00';
    };

    const columns = useMemo(
      () => [
        {
          name: 'Claim Number',
          key: 'claim_name',
          options: {
            width: 116,
            minWidth: 116,
            maxWidth: 116,
            shrink: false,
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Resident',
          key: 'tenant1',
          options: {
            minWidth: 100,
            flexGrow: 1,
            ellipsis: false,
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Policy ID',
          key: 'rider_id',
          options: {
            ...fitContentTextColumnOptions({ fitContentCharWidth: 7, maxWidth: 152, flexGrow: 0 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Status',
          key: 'claim_status',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 80, maxWidth: 96 }),
            sort: true,
            customBodyRenderer: (value) => (
              <Tooltip placement="top" showCarot title={value || 'Unknown'}>
                <div>
                  <Badge
                    background="rgba(243, 241, 244, 1)"
                    color={value !== 'Closed' ? 'parisGreen' : 'tomato'}
                    label={value}
                    rounded
                    textSize="medium"
                  />
                </div>
              </Tooltip>
            ),
          },
        },
        {
          name: 'File Status',
          key: 'claim_file_status',
          options: {
            width: 118,
            minWidth: 118,
            maxWidth: 118,
            shrink: false,
            sort: true,
            customBodyRenderer: (value) => {
              const fileStatus = getClaimFileStatusMeta(value);
              return (
                <Tooltip placement="top" showCarot title={value || 'Unknown'}>
                  <div>
                    <Box textAlign="center">
                      <Badge
                        background="#F3F1F4"
                        color={value === 'Open - Pending Decision' ? 'sunglow' : 'neutral'}
                        label={fileStatus.badge}
                        rounded
                        textSize="medium"
                      />
                    </Box>
                  </div>
                </Tooltip>
              );
            },
          },
        },
        {
          name: 'Application Type',
          key: 'app_type',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 92, maxWidth: 168 }),
            sort: true,
            customBodyRenderer: (value) => {
              const appType = getProgramAppTypeTableMeta(value, 'claim');
              return (
                <Tooltip title={appType.label} placement="top" showCarot>
                  <div>
                    <Box textAlign="center">
                      <Badge
                        background="rgba(243, 241, 244, 1)"
                        color={appType.badge}
                        label={appType.label}
                        rounded
                        textSize="medium"
                      />
                    </Box>
                  </div>
                </Tooltip>
              );
            },
          },
        },
        {
          name: 'Total Coverage',
          key: 'rider_damage_coverage_amount',
          options: {
            width: 120,
            minWidth: 120,
            maxWidth: 140,
            shrink: false,
            align: 'center',
            sort: true,
            exportValue: (value) => formatClaimDetailCurrency(value) ?? '$0',
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {formatClaimDetailCurrency(value) ?? '$0'}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Total Indemnity',
          key: 'total_indemnity_payments',
          options: {
            width: 120,
            minWidth: 120,
            maxWidth: 140,
            shrink: false,
            align: 'center',
            sort: true,
            exportValue: (value) => formatClaimDetailCurrency(value) ?? '$0',
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {formatClaimDetailCurrency(value) ?? '$0'}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Issued Date',
          key: 'bond_issue_date',
          options: {
            ...dateColumnOptions(),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Account',
          key: 'account_name',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 280 }),
            filter: false,
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Monthly Rent',
          key: 'monthly_rent',
          options: {
            width: 90,
            shrink: false,
            sort: true,
            exportValue: (value) => formatCurrency(value),
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                ${value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Operator',
          key: 'landlord_name',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 280 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Check Payee',
          key: 'who_should_the_check_be_made_out_to',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 280 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value || '—'}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Denial Reason',
          key: 'denial_reason_1',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 280 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value || '—'}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Actions',
          key: 'actions',
          options: {
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (claimIndex) => {
              const claim = claims?.[claimIndex];
              const disabled = !claim;

              return (
                <ActionsCell onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="View claim details" placement="top" showCarot>
                    <span>
                      <ActionIconButton
                        type="button"
                        disabled={disabled}
                        aria-label="View claim details"
                        onClick={disabled ? undefined : () => goDetailPage(claim)}
                      >
                        <IconGraphy
                          icon="FileFolder.Description"
                          style={{ color: disabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }}
                        />
                      </ActionIconButton>
                    </span>
                  </Tooltip>
                </ActionsCell>
              );
            },
          },
        },
      ],
      [claims, goDetailPage],
    );

    return (
      <>
        <Table
          loading={loading}
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText ? 'No claims match your search.' : 'No claims available yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          autoTooltipForDefaultCells
          defaultVisibleColumnKeys={CLAIMS_TABLE_DEFAULT_VISIBLE_KEYS}
          columnVisibilityStorageKey={CLAIMS_TABLE_STORAGE_KEY}
          columnWidthStorageKey={CLAIMS_TABLE_STORAGE_KEY}
          onRequestExportData={async () => {
            const exportRows = onRequestExportData ? await onRequestExportData() : claims;
            return (exportRows || []).map((claim, index) => buildTableRow(claim, index));
          }}
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const exportRows = await onRequestFullExportData();
                  return (exportRows || []).map((claim, index) => buildTableRow(claim, index));
                }
              : undefined
          }
          columns={columns}
          data={tableData}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Claims"
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Claims"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          onLoadFullDatasetTotal={onLoadFullDatasetTotal}
          isLoadingFullDatasetTotal={isLoadingFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

ClaimTable.displayName = 'ClaimTable';

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;
